// ----------------------------------------------------------------------
// Locale-aware currency input helpers.
//
// Thousands/decimal separators follow the country/currency the organization or
// user has configured. We derive a BCP-47 locale from the ISO-4217 currency
// (which is set per billing country during onboarding) and read the actual
// grouping/decimal symbols from Intl so the input matches the user's locale.
// ----------------------------------------------------------------------

const CURRENCY_LOCALE: Record<string, string> = {
  COP: 'es-CO',
  USD: 'en-US',
  EUR: 'es-ES',
  MXN: 'es-MX',
  ARS: 'es-AR',
  CLP: 'es-CL',
  PEN: 'es-PE',
  BRL: 'pt-BR',
  GBP: 'en-GB',
  PYG: 'es-PY',
  UYU: 'es-UY',
  BOB: 'es-BO',
  CRC: 'es-CR',
  GTQ: 'es-GT',
  DOP: 'es-DO',
  PAB: 'es-PA',
  VES: 'es-VE',
};

// Currencies that have no minor unit (cents). Inputs hide the decimals.
const ZERO_DECIMAL_CURRENCIES = new Set([
  'COP',
  'CLP',
  'PYG',
  'JPY',
  'KRW',
  'VND',
  'HUF',
  'ISK',
  'XAF',
  'XOF',
]);

const DEFAULT_LOCALE = 'es-CO';

/** Resolve a digit-grouping locale from an ISO-4217 currency code. */
export function localeForCurrency(currency?: string, fallback: string = DEFAULT_LOCALE): string {
  if (!currency) return fallback;
  return CURRENCY_LOCALE[currency.toUpperCase()] ?? fallback;
}

/** Number of decimal places for the given currency (0 for COP/CLP/JPY…, else 2). */
export function currencyDecimals(currency?: string): number {
  return ZERO_DECIMAL_CURRENCIES.has((currency ?? '').toUpperCase()) ? 0 : 2;
}

/** Grouping and decimal separators for a locale, read from Intl. */
export function getSeparators(locale: string): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(locale).formatToParts(11111.1);
  return {
    group: parts.find((p) => p.type === 'group')?.value ?? ',',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
  };
}

/** Canonical grouped display of a numeric value (used on blur / external sync). */
export function formatCurrencyValue(
  value: number | null | undefined,
  locale: string,
  maxDecimals: number
): string {
  if (value == null || Number.isNaN(value)) return '';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
    useGrouping: true,
  }).format(value);
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export type ParsedCurrencyInput = {
  /** Numeric value (null when the field is empty). */
  value: number | null;
  /** Live display string: integer part grouped, decimal tail kept as typed. */
  display: string;
};

/**
 * Sanitize a raw input string into a numeric value plus a locale-grouped
 * display string. Group separators are stripped; the locale decimal separator
 * is honored. Zero-decimal currencies reject any decimal input.
 */
export function parseCurrencyInput(
  raw: string,
  locale: string,
  maxDecimals: number
): ParsedCurrencyInput {
  const { group, decimal } = getSeparators(locale);

  // Keep only digits and the locale separators.
  const allowed = new RegExp(`[^0-9${escapeRegExp(group)}${escapeRegExp(decimal)}]`, 'g');
  let s = raw.replace(allowed, '');
  // Remove grouping separators.
  s = s.split(group).join('');

  if (maxDecimals === 0) {
    s = s.split(decimal).join('');
  }

  if (s === '') return { value: null, display: '' };

  let intPart = s;
  let decPart = '';
  let hasDecimal = false;

  if (maxDecimals > 0) {
    const idx = s.indexOf(decimal);
    if (idx >= 0) {
      hasDecimal = true;
      intPart = s.slice(0, idx);
      decPart = s
        .slice(idx + 1)
        .split(decimal)
        .join('')
        .slice(0, maxDecimals);
    }
  }

  // Strip leading zeros (but keep a single 0 before a decimal).
  intPart = intPart.replace(/^0+(?=\d)/, '');

  const numericStr = `${intPart || '0'}${decPart ? `.${decPart}` : ''}`;
  const num = Number(numericStr);
  const value = Number.isNaN(num) ? null : num;

  const groupedInt =
    intPart === ''
      ? ''
      : new Intl.NumberFormat(locale, { useGrouping: true, maximumFractionDigits: 0 }).format(
          Number(intPart)
        );

  let display = groupedInt;
  if (hasDecimal) display = `${groupedInt || '0'}${decimal}${decPart}`;

  return { value, display };
}
